import { useEffect, useMemo, useRef, useState } from "react";
import type { FocusEvent as ReactFocusEvent, KeyboardEvent as ReactKeyboardEvent, ReactNode } from "react";
import {
  Button,
  Checkbox,
  Dropdown,
  Field,
  Input,
  Option,
  Tab,
  TabList,
  Text,
  Title3,
  makeStyles,
  tokens,
  shorthands
} from "@fluentui/react-components";
import {
  AddRegular,
  CheckmarkCircleRegular,
  CopyRegular,
  DeleteRegular,
  DismissRegular,
  DocumentBulletListRegular,
  DocumentSearchRegular,
  NumberSymbolRegular,
  OpenRegular,
  PaintBrushRegular,
  TextBulletListSquareRegular,
} from "@fluentui/react-icons";
import { saveAs } from "file-saver";
import { applyAbstract } from "./commands/applyAbstract";
import { applyFigure, applyFigureCaption } from "./commands/applyFigure";
import { applyMinorSection } from "./commands/applyMinorSection";
import { applyBlockQuote, applyEquation, applyKeywords } from "./commands/applyObjects";
import { applyParagraph } from "./commands/applyParagraph";
import { applyReferences } from "./commands/applyReferences";
import { applySection } from "./commands/applySection";
import { applySubsection } from "./commands/applySubsection";
import { applyHeading4 } from "./commands/applyHeading4";
import { applyHeading5 } from "./commands/applyHeading5";
import { applyHeading6 } from "./commands/applyHeading6";
import { applyTable, applyTableCaption, applyTableNotes } from "./commands/applyTable";
import { applyTitle } from "./commands/applyTitle";
import { applySubtitle } from "./commands/applySubtitle";
import { applyAuthor } from "./commands/applyAuthor";
import { CREDIT_ROLES, normalizeOrcid, normalizeRor, validateEmail } from "./services/orcid";
import type { AuthorInput } from "./services/orcid";
import type { ValidationIssue } from "./types/SemanticTypes";
import { validateArticle } from "./validation/articleValidator";
import { validateTables } from "./validation/tableValidator";
import { numberingService } from "./services/numberingService";
import { wordService } from "./services/wordService";
import { EMPTY_PRODUCTION_METADATA, productionService } from "./services/productionService";
import type { ProductionMetadata } from "./services/productionService";
import { firstIssueDoi } from "./utils/doiSuggest";
import { semanticExporter } from "./export/semanticExporter";
import { jsonExporter } from "./export/jsonExporter";
import { jatsExporter } from "./export/jatsExporter";
import { htmlExporter } from "./export/htmlExporter";
import { xmlExporter } from "./export/xmlExporter";
import { journalRegistry } from "./data/journalRegistry";

// Sentinel row at the top of the volume/issue dropdown. Picking it clears the
// issue fields instead of copying an existing issue's values in.
const NEW_ISSUE_CHOICE = "__new_issue__";
const NEW_ISSUE_LABEL = "+ Create a new volume / issue";
const EMPTY_AUTHOR: AuthorInput = { name: "", affiliation: "", orcid: "", email: "", ror: "", roles: [] };

// Worker API gateway. Production build: same origin as the Pages-hosted taskpane
// ("/api"). Dev: set VITE_API_BASE=http://localhost:8787/api to hit `wrangler dev`.
const API_BASE = ((import.meta.env.VITE_API_BASE as string | undefined) ?? "/api").replace(/\/$/, "");
const API_ORIGIN = API_BASE.startsWith("http") ? new URL(API_BASE).origin : "";
// PDF URLs come back as absolute paths ("/api/pdf/<key>"); prefix the API origin
// only when the gateway is on a different host (dev).
const pdfHref = (path: string) => `${API_ORIGIN}${path}`;

const shakeAnimation = {
  "0%": { transform: "translateX(0)" },
  "20%": { transform: "translateX(-6px)" },
  "40%": { transform: "translateX(6px)" },
  "60%": { transform: "translateX(-6px)" },
  "80%": { transform: "translateX(6px)" },
  "100%": { transform: "translateX(0)" }
};

const checkDateValidity = (val: string): { valid: boolean; errorMsg?: string } => {
  const clean = val.replace(/\D/g, "");
  
  if (clean.length >= 2) {
    const day = parseInt(clean.slice(0, 2), 10);
    if (day < 1 || day > 31) {
      return { valid: false, errorMsg: "Day must be between 01 and 31." };
    }
  }
  
  if (clean.length >= 4) {
    const day = parseInt(clean.slice(0, 2), 10);
    const month = parseInt(clean.slice(2, 4), 10);
    if (month < 1 || month > 12) {
      return { valid: false, errorMsg: "Month must be between 01 and 12." };
    }
    
    const monthLengths = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (day > monthLengths[month - 1]) {
      return { valid: false, errorMsg: "Invalid day for the selected month." };
    }
  }
  
  if (clean.length === 8) {
    const day = parseInt(clean.slice(0, 2), 10);
    const month = parseInt(clean.slice(2, 4), 10);
    const year = parseInt(clean.slice(4, 8), 10);
    
    if (month === 2) {
      const isLeap = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
      const maxFebDays = isLeap ? 29 : 28;
      if (day > maxFebDays) {
        return { 
          valid: false, 
          errorMsg: isLeap 
            ? "February in a leap year only has 29 days." 
            : "February only has 28 days in non-leap years." 
        };
      }
    }
  }
  
  return { valid: true };
};

const isValidDateDDMMYYYY = (val: string): boolean => {
  if (!/^\d{2}-\d{2}-\d{4}$/.test(val)) return false;
  return checkDateValidity(val).valid;
};

const useStyles = makeStyles({
  shell: {
    minHeight: "100vh",
    backgroundColor: "#fafafa",
    color: tokens.colorNeutralForeground1
  },
  header: {
    padding: "18px 16px 12px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: "#fff"
  },
  main: {
    padding: "14px 14px 24px"
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "8px"
  },
  singleColumnGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "8px"
  },
  row: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap"
  },
  splitRow: {
    display: "grid",
    alignItems: "center",
    gridTemplateColumns: "1fr 1fr",
    gap: "8px",
    marginBottom: "12px"
  },
  // Breathing room under the "Already in OJS?" card above the Authors row.
  splitRowSpaced: {
    marginTop: "16px"
  },
  splitRowLabel: {
    minWidth: 0
  },
  splitRowButton: {
    width: "100%"
  },
  rowBottomSpace: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    marginBottom: "12px"
  },
  stack: {
    display: "grid",
    gap: "12px",
    marginTop: "12px"
  },
  productionJournalRow: {
    display: "grid",
    gridTemplateColumns: "1fr 44px",
    alignItems: "end",
    gap: "8px",
    marginBottom: "8px"
  },
  fetchButton: {
    minWidth: "44px",
    height: "32px"
  },
  addContributorButton: {
    marginBottom: "12px"
  },
  actionButton: {
    width: "100%",
    minHeight: "42px"
  },
  publishCard: {
    display: "grid",
    gap: "10px",
    padding: "14px",
    border: `1px solid ${tokens.colorBrandStroke2}`,
    borderLeft: `4px solid ${tokens.colorBrandBackground}`,
    borderRadius: "6px",
    backgroundColor: tokens.colorBrandBackground2
  },
  publishHint: {
    color: tokens.colorNeutralForeground2,
    lineHeight: "1.4"
  },
  publishTargetOk: {
    color: tokens.colorPaletteGreenForeground1,
    lineHeight: "1.4"
  },
  publishRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: "8px",
    alignItems: "center"
  },
  matchList: {
    display: "grid",
    gap: "6px"
  },
  matchCard: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    columnGap: "10px",
    rowGap: "2px",
    minHeight: "52px",
    padding: "8px 12px",
    alignContent: "center",
    textAlign: "left",
    whiteSpace: "normal",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2
  },
  matchId: { fontWeight: 700, gridRow: "1 / span 2", alignSelf: "center" },
  matchMeta: { fontSize: "11px", opacity: 0.85 },
  matchTitle: { gridColumn: "2", fontSize: "12px", lineHeight: "1.3", fontWeight: 400 },
  buttonLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    gap: "8px"
  },
  buttonMain: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
    flex: "1 1 auto"
  },
  buttonText: {
    fontSize: "inherit",
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  buttonIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    flex: "0 0 20px"
  },
  shortcut: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "2px 5px",
    borderRadius: "7px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground3,
    fontSize: "10px",
    lineHeight: "1",
    color: tokens.colorNeutralForeground1,
    flex: "0 0 auto"
  },
  shortcutOnDark: {
    backgroundColor: "rgba(255, 255, 255, 0.14)",
    border: "1px solid rgba(255, 255, 255, 0.28)",
    color: "#fff"
  },
  keycap: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "14px",
    height: "14px",
    padding: "0 3px",
    borderRadius: "4px",
    backgroundColor: "#fff",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxSizing: "border-box",
    color: tokens.colorNeutralForeground1
  },
  keycapOnDark: {
    backgroundColor: "rgba(255, 255, 255, 0.16)",
    border: "1px solid rgba(255, 255, 255, 0.32)",
    color: "#fff"
  },
  section: {
    padding: "12px 0",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`
  },
  authorCard: {
    display: "grid",
    gap: "6px",
    padding: "8px",
    marginBottom: "8px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: "6px",
    backgroundColor: "#fff"
  },
  authorCardHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  issueList: {
    display: "grid",
    gap: "8px",
    marginTop: "10px"
  },
  issue: {
    padding: "8px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: "6px",
    backgroundColor: "#fff",
    cursor: "pointer",
    textAlign: "left",
    width: "100%"
  },
  dangerButton: {
    backgroundColor: "#c62828",
    color: "#fff",
    marginTop: "12px",
    width: "100%",
    "&:hover": {
      backgroundColor: "#b71c1c",
      color: "#fff"
    }
  },
  toastWrap: {
    position: "sticky",
    bottom: "12px",
    marginTop: "12px",
    display: "flex",
    justifyContent: "center",
    zIndex: 10
  },
  toast: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    padding: "6px 12px",
    borderRadius: "8px",
    color: "#fff",
    boxSizing: "border-box",
    position: "relative",
    overflow: "hidden"
  },
  toastSuccess: {
    backgroundColor: "#0f5132",
    border: "1px solid rgba(73, 231, 153, 0.55)",
    boxShadow: "0 0 0 1px rgba(73, 231, 153, 0.2), 0 0 18px rgba(73, 231, 153, 0.45)"
  },
  toastError: {
    backgroundColor: "#7f1d1d",
    border: "1px solid rgba(248, 113, 113, 0.6)",
    boxShadow: "0 0 0 1px rgba(248, 113, 113, 0.22), 0 0 18px rgba(248, 113, 113, 0.42)"
  },
  toastPending: {
    backgroundColor: "#1f2937",
    border: "1px solid rgba(148, 163, 184, 0.45)",
    boxShadow: "0 0 0 1px rgba(148, 163, 184, 0.18), 0 0 16px rgba(15, 23, 42, 0.24)"
  },
  toastContent: {
    display: "grid",
    gap: "2px",
    position: "relative",
    zIndex: 1
  },
  toastUrlBar: {
    marginTop: "8px",
    display: "grid",
    gridTemplateColumns: "1fr auto auto",
    gap: "8px",
    alignItems: "center",
    padding: "8px 10px",
    borderRadius: "8px",
    backgroundColor: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.16)"
  },
  toastUrlText: {
    minWidth: 0,
    color: "#fff",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  toastUrlButton: {
    color: "#fff",
    minWidth: "32px",
    width: "32px",
    height: "32px"
  },
  toastOpenLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    color: "#fff",
    backgroundColor: "rgba(255,255,255,0.18)",
    borderRadius: "4px",
    padding: "4px 10px",
    fontWeight: 600,
    textDecoration: "none",
    whiteSpace: "nowrap"
  },
  toastDismiss: {
    color: "#fff",
    minWidth: "28px",
    width: "28px",
    height: "28px",
    position: "relative",
    zIndex: 1
  },
  // Indeterminate "filling/draining" bar shown while an action is running, so a
  // pending toast reads as live progress instead of a flat black bar.
  toastIndeterminate: {
    position: "absolute",
    left: 0,
    bottom: 0,
    height: "3px",
    width: "48%",
    borderRadius: "10px",
    backgroundColor: "rgba(147, 197, 253, 0.95)",
    boxShadow: "0 0 12px rgba(96, 165, 250, 0.85)",
    animationDuration: "1.15s",
    animationIterationCount: "infinite",
    animationTimingFunction: "cubic-bezier(.4, 0, .2, 1)",
    willChange: "transform",
    animationName: {
      "0%": { transform: "translate3d(-110%, 0, 0) scaleX(0.75)" },
      "50%": { transform: "translate3d(55%, 0, 0) scaleX(1.2)" },
      "100%": { transform: "translate3d(230%, 0, 0) scaleX(0.75)" }
    }
  },
  shakeError: {
    animationName: shakeAnimation,
    animationDuration: "0.3s",
    animationIterationCount: 1,
    animationTimingFunction: "ease-in-out"
  },
  dateErrorInput: {
    ...shorthands.borderColor("#d13438"),
    color: "#d13438",
    "&::after": {
      borderBottomColor: "#d13438"
    },
    ":hover": {
      ...shorthands.borderColor("#d13438")
    },
    ":focus-within": {
      ...shorthands.borderColor("#d13438")
    }
  },
  dateRow: {
    display: "flex",
    alignItems: "center",
    gap: "4px"
  },
  dateSegmentDay: {
    width: "60px"
  },
  dateSegmentMonth: {
    width: "60px"
  },
  dateSegmentYear: {
    width: "76px"
  },
  dateSegmentSeparator: {
    color: "#666",
    fontWeight: 600
  },
  crossmarkCheckbox: {
    display: "flex",
    alignItems: "center",
    minHeight: "32px",
    paddingLeft: 0
  }
});

type TabValue = "tag" | "contributors" | "production" | "validate" | "export" | "publish";
type ShortcutAction = {
  key: string;
  run: () => void;
};
type OjsSection = {
  id?: number | string;
  title: string;
  abbrev?: string;
};
type OjsIssue = {
  id?: number | null;
  volume: string;
  number: string;
  year: string;
  lastPage?: number | null;
  nextStartPage?: number | null;
  pageNumberStatus?: "ok" | "empty" | "partial" | "unparseable";
  lastPublicationDate?: string | null;
  suggestedPublicationDate?: string | null;
  publicationDateStatus?: "ok" | "empty" | "partial" | "unparseable";
  lastDoi?: string | null;
  nextDoi?: string | null;
  doiStatus?: "ok" | "empty" | "partial" | "unparseable";
};
type NoticeTone = "pending" | "success" | "error";
type Notice = {
  message: string;
  tone: NoticeTone;
  visible: boolean;
  sticky?: boolean;
  url?: string;
};
type ExportArtifacts = {
  json: Blob;
  jats: Blob;
  xml: Blob;
  html: Blob;
};
type JsonSaveFileHandle = {
  createWritable: () => Promise<{
    write: (data: Blob) => Promise<void>;
    close: () => Promise<void>;
  }>;
};
type JsonSavePickerWindow = Window &
  typeof globalThis & {
    showSaveFilePicker?: (options: {
      suggestedName: string;
      types: Array<{
        description: string;
        accept: Record<string, string[]>;
      }>;
    }) => Promise<JsonSaveFileHandle>;
  };

// Crossref Crossmark domain for a journal = the publisher's eTLD+1, taken from
// the registry baseUrl (e.g. journal.thelawbrigade.com -> thelawbrigade.com,
// jaira.org.uk -> jaira.org.uk). Returns "" if unknown.
const TWO_LEVEL_TLDS = ["co.uk", "org.uk", "ac.uk", "gov.uk", "co.in", "org.in", "net.in", "com.au"];
function crossmarkDomainForJournal(journalName: string): string {
  const entry = journalRegistry.find((j) => j.name === journalName);
  if (!entry?.baseUrl) return "";
  try {
    const parts = new URL(entry.baseUrl).hostname.split(".").filter(Boolean);
    if (parts.length <= 2) return parts.join(".");
    const take = TWO_LEVEL_TLDS.includes(parts.slice(-2).join(".")) ? 3 : 2;
    return parts.slice(-take).join(".");
  } catch {
    return "";
  }
}

export default function App() {
  const styles = useStyles();
  const [tab, setTab] = useState<TabValue>("tag");
  const [notice, setNotice] = useState<Notice>({ message: "", tone: "pending", visible: false });
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [authors, setAuthors] = useState<AuthorInput[]>([{ ...EMPTY_AUTHOR }]);
  const [captionDraft, setCaptionDraft] = useState("");
  const [exportArtifacts] = useState<ExportArtifacts | null>(null);
  const [production, setProduction] = useState<ProductionMetadata>({ ...EMPTY_PRODUCTION_METADATA });
  const [sectionOptions, setSectionOptions] = useState<OjsSection[]>([]);
  const [issueOptions, setIssueOptions] = useState<OjsIssue[]>([]);
  const [issueDisabled, setIssueDisabled] = useState(false);
  // Which row of the volume/issue dropdown is showing: "" (nothing picked yet),
  // NEW_ISSUE_CHOICE, or the index of an existing issue. The dropdown has to be
  // controlled so "Create a new volume / issue" can stay visibly selected.
  const [issueChoice, setIssueChoice] = useState("");
  const [generatedPdf, setGeneratedPdf] = useState<{ key: string; url: string } | null>(null);
  // Publish INTO a submission that is already in the OJS pipeline (e.g. the
  // author paid the publication fee there) instead of creating a new one.
  const [existingSubmissionId, setExistingSubmissionId] = useState("");
  const [submissionMatches, setSubmissionMatches] = useState<
    Array<{ id: number; title: string; stage: string; status: string; published: boolean }>
  >([]);
  // Contributors tab: the ID typed before a fetch. Once the fetch succeeds the
  // ID is stored on the document (production.ojsSubmissionId) and Publish reuses
  // it, so the same paper can never be published into a different submission.
  const [submissionIdDraft, setSubmissionIdDraft] = useState("");
  const [ojsSubmissionInfo, setOjsSubmissionInfo] = useState<
    { id: number; title: string; stage: string; status: string; published: boolean } | null
  >(null);
  const [doiDisabled, setDoiDisabled] = useState(false);
  // Kept for recomputing a DOI suggestion when the user types a brand-new
  // volume/issue that isn't in issueOptions yet (see recomputeDoiSuggestion).
  const [doiPrefixState, setDoiPrefixState] = useState("");
  const [journalShortCode, setJournalShortCode] = useState("");
  const [detailsFetched, setDetailsFetched] = useState(false);
  const [dateError, setDateError] = useState(false);
  const [dateShaking, setDateShaking] = useState(false);
  const [dateErrorMessage, setDateErrorMessage] = useState("");

  const updateAuthor = (index: number, field: keyof AuthorInput, value: any) =>
    setAuthors((prev) => prev.map((a, i) => (i === index ? { ...a, [field]: value } : a)));
  const addAuthor = () => setAuthors((prev) => [...prev, { ...EMPTY_AUTHOR }]);
  const removeAuthor = (index: number) =>
    setAuthors((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  const updateProduction = (field: keyof ProductionMetadata, value: string) =>
    setProduction((prev) => ({ ...prev, [field]: value }));
  const updateProductionSection = (section: OjsSection) =>
    setProduction((prev) => ({
      ...prev,
      section: section.title,
      sectionId: String(section.id ?? ""),
      sectionAbbrev: section.abbrev ?? ""
    }));
  const updateStartPage = (value: string) => setProduction((prev) => ({ ...prev, startPage: value }));

  // Recomputes the DOI suggestion when Volume/Issue/Publication-Date are
  // typed directly instead of picked from the "Existing volume/issue"
  // dropdown (that dropdown's onOptionSelect already handles its own case).
  // - Volume/issue matches an already-fetched OJS issue -> reuse its
  //   precomputed nextDoi (same last+1 logic the dropdown uses).
  // - No match (a brand-new volume/issue) -> build serial 01 locally, but
  //   only once the year (from Publication Date) and doiPrefix/shortCode
  //   (from Fetch Journal Details) are all known — otherwise leave DOI as-is
  //   rather than fabricate a partial value.
  const recomputeDoiSuggestion = () => {
    if (doiDisabled || !detailsFetched) return;
    const volume = production.volume.trim();
    if (!volume) return;
    const issueNumber = issueDisabled ? "" : production.issue.trim();
    const normalize = (n: string) => (n && n !== "0" ? n : "");
    const matched = issueOptions.find(
      (iss) => iss.volume.trim() === volume && normalize((iss.number || "").trim()) === normalize(issueNumber)
    );
    if (matched) {
      setProduction((prev) => ({ ...prev, doi: matched.nextDoi ?? "" }));
      return;
    }
    const year = production.publicationDate.split("-")[2] || "";
    const doi = firstIssueDoi(volume, issueNumber, year, doiPrefixState, journalShortCode);
    if (doi) setProduction((prev) => ({ ...prev, doi }));
  };

  const issueLabel = (iss: OjsIssue) => {
    const parts: string[] = [];
    if (iss.volume) parts.push(`Vol. ${iss.volume}`);
    if (iss.number) parts.push(`No. ${iss.number}`);
    const base = parts.join(" · ") || "Issue";
    return iss.year ? `${base} (${iss.year})` : base;
  };
  const issueChoiceLabel =
    issueChoice === NEW_ISSUE_CHOICE
      ? NEW_ISSUE_LABEL
      : issueChoice !== "" && issueOptions[Number(issueChoice)]
        ? issueLabel(issueOptions[Number(issueChoice)])
        : "";

  const journalDropdown = (label = "Journal") => (
    <Field label={label}>
      <Dropdown
        value={production.journal}
        selectedOptions={production.journal ? [production.journal] : []}
        placeholder="Select journal"
        onOptionSelect={(_, data) => {
          setSectionOptions([]);
          setDetailsFetched(false);
          setProduction((prev) => ({ ...prev, journal: String(data.optionValue ?? ""), section: "" }));
        }}
      >
        {journalRegistry.map((journal) => (
          <Option key={`${journal.shortCode}-${journal.baseUrl}`} value={journal.name} text={journal.name}>
            {journal.name} ({journal.shortCode})
          </Option>
        ))}
      </Dropdown>
    </Field>
  );

  const isMac = useMemo(() => typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform), []);
  const shortcutPrefix = useMemo(() => (isMac ? "Control+Option+" : "Ctrl+Alt+"), [isMac]);
  const shortcutMods = useMemo(() => (isMac ? ["⌃", "⌥"] : ["Ctrl", "Alt"]), [isMac]);

  const shortcutTitle = (label: string, key: string) => `${label} (${shortcutPrefix}${key.toUpperCase()})`;
  const showNotice = (message: string, tone: NoticeTone, extras: Partial<Notice> = {}) =>
    setNotice({ message, tone, visible: true, ...extras });
  const hideNotice = () => setNotice((prev) => ({ ...prev, visible: false }));
  const buttonText = (label: string, key: string, icon?: ReactNode, inverted = false) => (
    <span className={styles.buttonLabel}>
      <span className={styles.buttonMain}>
        {icon ? <span className={styles.buttonIcon}>{icon}</span> : null}
        <span className={styles.buttonText}>{label}</span>
      </span>
      <span className={[styles.shortcut, inverted ? styles.shortcutOnDark : ""].join(" ")} aria-hidden="true">
        {shortcutMods.map((mod) => (
          <span key={mod} className={[styles.keycap, inverted ? styles.keycapOnDark : ""].join(" ")}>{mod}</span>
        ))}
        <span className={[styles.keycap, inverted ? styles.keycapOnDark : ""].join(" ")}>{key.toUpperCase()}</span>
      </span>
    </span>
  );

  useEffect(() => {
    if (!notice.visible || notice.tone === "pending" || notice.sticky) return;
    // Success bars flash for 1s and auto-close; errors linger a bit so they're
    // readable. Sticky bars (Export / Publish) never auto-close.
    const timer = window.setTimeout(() => hideNotice(), notice.tone === "success" ? 1000 : 4000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const copyText = async (value: string) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }
    const area = document.createElement("textarea");
    area.value = value;
    area.setAttribute("readonly", "true");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    document.execCommand("copy");
    document.body.removeChild(area);
  };

  const run = async (label: string, action: () => Promise<unknown>) => {
    try {
      showNotice(`${label}...`, "pending");
      const result = await action();
      if (Array.isArray(result)) setIssues(result as ValidationIssue[]);
      if (typeof result === "string") {
        showNotice(result, "success");
      } else if (result && typeof result === "object") {
        const payload = result as Partial<Notice>;
        showNotice(payload.message || `${label} complete.`, "success", payload);
      } else {
        showNotice(`${label} complete.`, "success");
      }
    } catch (error) {
      // Office.js wraps native Word runtime failures in a generic
      // OfficeExtension.Error whose top-level `.message` is often just the
      // error code itself (e.g. "GeneralException") with the actual cause in
      // `.debugInfo` — log the full object so it's visible in the browser
      // console (Safari Web Inspector / DevTools attached to the task pane)
      // even though the notice banner can only show a short string.
      // eslint-disable-next-line no-console
      console.error(`${label} failed:`, error);
      const debugInfo = (error as { debugInfo?: { message?: string; errorLocation?: string } })?.debugInfo;
      const base = error instanceof Error ? error.message : String(error);
      const detail = debugInfo?.message ? ` — ${debugInfo.message}${debugInfo.errorLocation ? ` (${debugInfo.errorLocation})` : ""}` : "";
      showNotice(base + detail, "error");
    }
  };

  const insertAuthors = () =>
    run("Insert Authors", async () => {
      await wordService.insertAuthors(authors);
      setAuthors([{ ...EMPTY_AUTHOR }]);
    });
  const loadAuthors = () =>
    run("Load Authors", async () => {
      const loaded = await wordService.readAuthors();
      setAuthors(loaded.length > 0 ? loaded : [{ ...EMPTY_AUTHOR }]);
      if (loaded.length === 0) throw new Error("No authors found in the document.");
    });
  const updateAuthorsInDoc = () => run("Update Authors", () => wordService.updateAuthors(authors));

  // Pull the submission's contributors out of OJS into the pane. Each one keeps
  // its OJS contributor id, which Insert/Update writes into the author content
  // control — publish then updates that exact contributor instead of matching by
  // name (the matching that produced duplicated co-authors). The ID is locked to
  // the document so Publish targets the same submission.
  const fetchContributorsFromOjs = async () => {
    const journal = production.appliedJournal || production.journal;
    if (!journal) throw new Error("Select the journal in the Production tab first.");
    const id = submissionIdDraft.trim();
    if (!/^\d+$/.test(id)) throw new Error("Type the OJS submission ID first, e.g. 899.");
    const response = await fetch(`${API_BASE}/fetch-ojs-contributors`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ journal, submissionId: id })
    }).catch(() => {
      throw new Error("Editor service not reachable. Check your connection and try again.");
    });
    const payload = (await response.json().catch(() => null)) as {
      ok?: boolean;
      error?: string;
      submissionId?: number;
      title?: string;
      stage?: string;
      status?: string;
      published?: boolean;
      contributors?: Array<{ ojsContributorId?: number; name: string; affiliation: string; email: string; orcid: string; ror: string }>;
    } | null;
    if (!response.ok || !payload?.ok) throw new Error(payload?.error || `OJS lookup failed (HTTP ${response.status}).`);
    const fetched = payload.contributors ?? [];
    if (fetched.length === 0) throw new Error(`Submission ${id} has no contributors in OJS yet.`);

    // Replace the pane outright: it must mirror what OJS holds, or the ids and
    // the rows stop lining up.
    setAuthors(fetched.map((c) => ({ ...EMPTY_AUTHOR, ...c, roles: [] })));
    setOjsSubmissionInfo({
      id: payload.submissionId ?? Number(id),
      title: payload.title ?? "",
      stage: payload.stage ?? "",
      status: payload.status ?? "",
      published: Boolean(payload.published)
    });
    const next = { ...production, ojsSubmissionId: id };
    setProduction(next);
    await productionService.save(doiDisabled ? { ...next, doi: "" } : next);
    return payload.published
      ? `Loaded ${fetched.length} contributors, but submission ${id} is ALREADY published — publishing into it will fail.`
      : `Loaded ${fetched.length} contributors from submission ${id}. Edit here, then Insert/Update in the document.`;
  };

  // Release the lock so a different submission can be fetched. Clears the ids
  // held in the pane; ids already written into the document's author controls
  // are replaced the next time you Insert/Update authors.
  const unlockOjsSubmission = () =>
    run("Unlock Submission", async () => {
      const next = { ...production, ojsSubmissionId: "" };
      setProduction(next);
      await productionService.save(doiDisabled ? { ...next, doi: "" } : next);
      setAuthors((prev) => prev.map((a) => ({ ...a, ojsContributorId: undefined })));
      setOjsSubmissionInfo(null);
      setSubmissionIdDraft("");
      return "Submission unlocked. Fetch another ID, or publish as a new submission.";
    });
  const applyProduction = () =>
    run("Apply Production", async () => {
      const metadata = doiDisabled ? { ...production, doi: "" } : production;
      await productionService.apply(metadata);
      setProduction((p) => ({ ...p, doi: doiDisabled ? "" : p.doi, appliedJournal: p.journal.trim() }));
    });
  const updateProductionData = () =>
    run("Update Production Data", async () => {
      const metadata = doiDisabled ? { ...production, doi: "" } : production;
      await productionService.apply(metadata);
      setProduction((p) => ({ ...p, doi: doiDisabled ? "" : p.doi, appliedJournal: p.journal.trim() }));
      return "Production data updated.";
    });
  const saveProduction = () =>
    run("Save Production", () => productionService.save(doiDisabled ? { ...production, doi: "" } : production));
  const removeProductionData = () =>
    run("Remove Production Data", async () => {
      await productionService.remove();
      setProduction((p) => ({ ...p, appliedJournal: "" }));
      return "Production data removed — header, footer and DOI cleared. Publish unlocked.";
    });

  const triggerShake = () => {
    setDateShaking(false);
    setTimeout(() => {
      setDateShaking(true);
    }, 10);
  };

  // Day/month/year as three independent fields instead of one masked
  // "DD-MM-YYYY" text input — deleting a digit in one segment used to shift
  // every following digit left across day/month/year boundaries (backspace
  // in "12" of "12-11-2025" produced "21-12-025"), which read as the field
  // silently corrupting itself. Each segment now owns only its own digits.
  const [pubDay = "", pubMonth = "", pubYear = ""] = production.publicationDate.split("-");
  // Auto-advance focuses the next segment from inside onChange, so the blur
  // handler that runs next still closes over the pre-keystroke state ("3"
  // instead of "30") and would pad it to "03". Keep the freshest segments in
  // a ref so blur pads what was actually typed.
  const pubDateRef = useRef({ day: pubDay, month: pubMonth, year: pubYear });
  pubDateRef.current = { day: pubDay, month: pubMonth, year: pubYear };
  const dayInputRef = useRef<HTMLInputElement>(null);
  const monthInputRef = useRef<HTMLInputElement>(null);
  const yearInputRef = useRef<HTMLInputElement>(null);

  const combinePublicationDate = (day: string, month: string, year: string) =>
    day || month || year ? `${day}-${month}-${year}` : "";

  const validatePublicationDate = (day: string, month: string, year: string) => {
    if (day.length === 2 && month.length === 2 && year.length === 4) {
      const check = checkDateValidity(`${day}-${month}-${year}`);
      if (!check.valid) {
        setDateError(true);
        setDateErrorMessage(check.errorMsg || "Invalid date");
        triggerShake();
        return;
      }
    }
    setDateError(false);
    setDateErrorMessage("");
  };

  const updatePublicationDatePart = (part: "day" | "month" | "year", raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, part === "year" ? 4 : 2);
    const current = pubDateRef.current;
    const day = part === "day" ? digits : current.day;
    const month = part === "month" ? digits : current.month;
    const year = part === "year" ? digits : current.year;
    pubDateRef.current = { day, month, year };
    updateProduction("publicationDate", combinePublicationDate(day, month, year));
    validatePublicationDate(day, month, year);
    // Auto-advance to the next segment once this one is full — typing stays
    // fluid without requiring Tab, same as most date-of-birth style widgets.
    if (part === "day" && digits.length === 2) monthInputRef.current?.focus();
    if (part === "month" && digits.length === 2) yearInputRef.current?.focus();
  };

  const handlePublicationDateSegmentKeyDown = (
    part: "month" | "year",
    e: ReactKeyboardEvent<HTMLInputElement>
  ) => {
    // Backspace on an already-empty segment jumps back to the previous one,
    // so clearing a full date reads naturally right-to-left.
    if (e.key === "Backspace" && (e.target as HTMLInputElement).value === "") {
      if (part === "month") dayInputRef.current?.focus();
      if (part === "year") monthInputRef.current?.focus();
    }
  };

  const handlePublicationDateBlur = (part: "day" | "month" | "year", e: ReactFocusEvent<HTMLInputElement>) => {
    // A lone digit in day/month is a normal thing to type ("3" for the 3rd) —
    // pad it instead of complaining that the date is incomplete.
    const current = pubDateRef.current;
    let day = current.day;
    let month = current.month;
    const year = current.year;
    if (part === "day" && day.length === 1) day = `0${day}`;
    if (part === "month" && month.length === 1) month = `0${month}`;
    if (day !== current.day || month !== current.month) {
      pubDateRef.current = { day, month, year };
      updateProduction("publicationDate", combinePublicationDate(day, month, year));
    }
    // Moving between the day/month/year boxes is still "editing the date" —
    // only judge completeness once focus leaves the whole date field.
    const next = e.relatedTarget as HTMLInputElement | null;
    const stillEditing = !!next && [dayInputRef.current, monthInputRef.current, yearInputRef.current].includes(next);
    if (stillEditing) {
      validatePublicationDate(day, month, year);
      return;
    }
    if (!day && !month && !year) {
      setDateError(false);
      setDateErrorMessage("");
      return;
    }
    if (day.length < 2 || month.length < 2 || year.length < 4) {
      setDateError(true);
      setDateErrorMessage("Publication date must be complete (DD-MM-YYYY).");
      triggerShake();
      return;
    }
    validatePublicationDate(day, month, year);
    // A brand-new volume/issue's DOI suggestion needs the year, which may
    // only just have become available now that the date is complete.
    recomputeDoiSuggestion();
  };

  const baseFilenameFromUrl = (urlValue: string) => {
    if (!urlValue.trim()) return "article";
    try {
      const parsed = new URL(urlValue);
      urlValue = parsed.pathname;
    } catch {
      urlValue = urlValue.split("?")[0].split("#")[0];
    }
    const rawName = decodeURIComponent(urlValue.split(/[\\/]/).filter(Boolean).pop() || "").trim();
    const baseName = rawName
      .replace(/\.(docx|docm|dotx|dotm)$/i, "")
      .split("")
      .map((char) => (/[<>:"/\\|?*]/.test(char) || char.charCodeAt(0) < 32 ? "-" : char))
      .join("")
      .trim();
    return baseName || "article";
  };

  const getDocumentFileInfo = async () =>
    new Promise<{ baseName: string; url: string }>((resolve) => {
      Office.context.document.getFilePropertiesAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve({ baseName: baseFilenameFromUrl(result.value.url), url: result.value.url || "" });
          return;
        }
        const url = Office.context.document.url || "";
        resolve({ baseName: baseFilenameFromUrl(url), url });
      });
    });

  const getDocumentBaseFilename = async () => (await getDocumentFileInfo()).baseName;

  const buildJsonText = async () => {
    if (exportArtifacts?.json) return exportArtifacts.json.text();
    const article = await semanticExporter.buildArticleJson();
    return jsonExporter.export(article);
  };

  const saveBlobWithPickerOrDownload = async (blob: Blob, filename: string, description: string, extension: string, mime: string) => {
    const picker = (window as JsonSavePickerWindow).showSaveFilePicker;
    if (picker) {
      const handle = await picker({
        suggestedName: filename,
        types: [
          {
            description,
            accept: { [mime]: [extension] }
          }
        ]
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return;
    }
    saveAs(blob, filename);
  };

  const openExportDialog = async (
    content: string,
    filename: string,
    mime: string,
    encoding: "text" | "base64",
    title: string,
    downloadLabel: string
  ) =>
    new Promise<void>((resolve, reject) => {
      const url = `${window.location.origin}${import.meta.env.BASE_URL}json-export-dialog.html`;
      Office.context.ui.displayDialogAsync(url, { height: 58, width: 46, displayInIframe: false }, (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(result.error?.message || `${title} export dialog could not open.`));
          return;
        }

        const dialog = result.value;
        let sent = false;
        const sendJson = () => {
          if (sent) return;
          sent = true;
          const chunkSize = 30000;
          const total = Math.ceil(content.length / chunkSize);
          dialog.messageChild(JSON.stringify({ type: "json-export-start", filename, mime, encoding, title, downloadLabel, total }));
          for (let index = 0; index < total; index += 1) {
            dialog.messageChild(
              JSON.stringify({
                type: "json-export-chunk",
                index,
                chunk: content.slice(index * chunkSize, (index + 1) * chunkSize)
              })
            );
          }
          dialog.messageChild(JSON.stringify({ type: "json-export-end" }));
          resolve();
        };

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (args) => {
          if ("message" in args) {
            try {
              const message = JSON.parse(args.message) as { type?: string };
              if (message.type === "json-export-ready") sendJson();
            } catch {
              // Ignore non-JSON dialog messages.
            }
          }
        });
        dialog.addEventHandler(Office.EventType.DialogEventReceived, (args) => {
          if ("error" in args && !sent) reject(new Error(`${title} export dialog closed before file was prepared.`));
        });
      });
    });

  const exportJson = async () => {
    const [jsonText, baseName] = await Promise.all([buildJsonText(), getDocumentBaseFilename()]);
    const filename = `${baseName}.json`;
    const blob = new Blob([jsonText], { type: "application/json" });
    if (!((window as JsonSavePickerWindow).showSaveFilePicker) && isMac) {
      await openExportDialog(jsonText, filename, "application/json", "text", "Export JSON", "Download JSON");
      return;
    }
    try {
      await saveBlobWithPickerOrDownload(blob, filename, "JSON file", ".json", "application/json");
    } catch {
      await openExportDialog(jsonText, filename, "application/json", "text", "Export JSON", "Download JSON");
    }
  };

  const exportXml = async () => {
    if (!exportArtifacts) throw new Error("Exports are still preparing. Open Export tab and wait a moment.");
    const baseName = await getDocumentBaseFilename();
    const filename = `${baseName}.xml`;
    const xmlText = await exportArtifacts.xml.text();
    const blob = new Blob([xmlText], { type: "application/xml" });
    if (!((window as JsonSavePickerWindow).showSaveFilePicker) && isMac) {
      await openExportDialog(xmlText, filename, "application/xml", "text", "Export XML", "Download XML");
      return;
    }
    try {
      await saveBlobWithPickerOrDownload(blob, filename, "XML file", ".xml", "application/xml");
    } catch {
      await openExportDialog(xmlText, filename, "application/xml", "text", "Export XML", "Download XML");
    }
  };

  const exportPdf = async () => {
    // One deliberate scan on click. Background export preparation previously
    // overlapped this scan and could freeze Mac Word on long documents.
    const fullArticle = await semanticExporter.buildArticleJson({ skipSections: true });
    // Author email addresses are for OJS matching/publishing ONLY — never send
    // them to the PDF renderer (the server strips them again as a backstop).
    const article = {
      ...fullArticle,
      authorDetails: (fullArticle.authorDetails || []).map(({ email: _email, ...rest }) => rest)
    };
    // Crossmark badge domain: publisher eTLD+1 from the selected journal's
    // registry baseUrl (e.g. journal.thelawbrigade.com -> thelawbrigade.com).
    const productionForRender = { ...production, crossmarkDomain: crossmarkDomainForJournal(production.journal) };
    // PDF template/theme is no longer chosen here — the Worker resolves it
    // server-side from production.journal against the journal registry's
    // PDF Theme column (see scripts/sync_journal_registry.py).
    const response = await fetch(`${API_BASE}/generate-pdf`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ article, production: productionForRender })
    }).catch(() => {
      throw new Error("Editor service not reachable. Check your connection and try Export PDF again.");
    });
    const payload = (await response.json().catch(() => null)) as { ok?: boolean; key?: string; url?: string; endPage?: string; error?: string } | null;
    if (!response.ok || !payload?.ok || !payload.key || !payload.url) {
      throw new Error(payload?.error || `PDF generation failed with HTTP ${response.status}.`);
    }
    setGeneratedPdf({ key: payload.key, url: payload.url });
    if (payload.endPage) {
      setProduction((prev) => ({ ...prev, endPage: payload.endPage || prev.endPage }));
    }
    const href = pdfHref(payload.url);
    openInSystemBrowser(href);
    return { message: "PDF Generated — click to preview.", sticky: true, url: href };
  };

  // Open a URL in the user's real default browser. On Mac Word, window.open from
  // the task pane lands in Word's own sandboxed WebView (the preview popup whose
  // download button does nothing); openBrowserWindow hands off to Safari/Chrome
  // where the native PDF viewer and downloads work. Fall back to window.open on
  // builds without the OpenBrowserWindow API.
  const openInSystemBrowser = (href: string) => {
    const ui = Office?.context?.ui as { openBrowserWindow?: (url: string) => void } | undefined;
    if (ui?.openBrowserWindow) ui.openBrowserWindow(href);
    else window.open(href, "_blank");
  };

  const previewPdf = async () => {
    if (!generatedPdf) throw new Error("Generate the PDF first.");
    const href = pdfHref(generatedPdf.url);
    // Programmatic open (openBrowserWindow / window.open) is unreliable inside
    // Word's task pane on Mac, so also surface the link in the toast — the anchor
    // there opens the system browser reliably when clicked.
    openInSystemBrowser(href);
    return { message: "Preview ready — click the link to open in your browser.", sticky: true, url: href };
  };

  const downloadPdf = async () => {
    if (!generatedPdf) throw new Error("Generate the PDF first.");
    // ?download=1 -> worker sets Content-Disposition: attachment, so the browser
    // saves the file (Save dialog) instead of rendering inline.
    const href = `${pdfHref(generatedPdf.url)}?download=1`;
    openInSystemBrowser(href);
    return { message: "Download ready — click the link to save the PDF.", sticky: true, url: href };
  };

  const discardGeneratedPdf = async () => {
    if (!generatedPdf) throw new Error("No generated PDF to discard.");
    const response = await fetch(`${API_BASE}/discard-pdf`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key: generatedPdf.key })
    }).catch(() => {
      throw new Error("Editor service not reachable. Check your connection and try again.");
    });
    const payload = (await response.json().catch(() => null)) as { ok?: boolean; message?: string; error?: string } | null;
    if (!response.ok || !payload?.ok) {
      throw new Error(payload?.error || `Discard failed with HTTP ${response.status}.`);
    }
    setGeneratedPdf(null);
    return payload.message || "Generated PDF discarded from storage.";
  };


  const fetchOjsSections = async () => {
    const response = await fetch(`${API_BASE}/sections`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ journal: production.journal })
    }).catch(() => {
      throw new Error("Editor service not reachable. Check your connection and fetch sections again.");
    });
    const payload = (await response.json().catch(() => null)) as {
      ok?: boolean;
      sections?: OjsSection[];
      doiPrefix?: string;
      doiEnabled?: boolean;
      licenseUrl?: string;
      issues?: OjsIssue[];
      usesIssues?: boolean | null;
      pageNumbersAvailable?: boolean;
      publisherName?: string;
      publisherCountry?: string;
      shortCode?: string;
      source?: string;
      error?: string;
    } | null;
    if (!response.ok || !payload?.ok || !payload.sections?.length) {
      throw new Error(payload?.error || "No sections returned by OJS.");
    }
    setSectionOptions(payload.sections);
    setIssueOptions(payload.issues ?? []);
    // Grey the Issue field only when OJS clearly shows a volume-only journal
    // (existing issues exist and none carry an issue number).
    setIssueDisabled(payload.usesIssues === false);

    // Normalize the prefix so it always ends with "/" (so suffix-only entry works).
    let prefix = (payload.doiPrefix || "").trim();
    if (prefix && !prefix.endsWith("/")) prefix = `${prefix}/`;
    setDoiPrefixState(prefix);
    setJournalShortCode((payload.shortCode || "").trim());

    // Grey out ONLY when OJS explicitly reports DOI disabled — never just because
    // a prefix couldn't be fetched (that wrongly blocked DOI-active journals).
    const disabledDoi = payload.doiEnabled === false;
    setDoiDisabled(disabledDoi);

    setProduction((prev) => {
      const selected =
        payload.sections!.find(
          (section) =>
            section.title === prev.section ||
            String(section.id ?? "") === prev.sectionId ||
            section.abbrev === prev.sectionAbbrev ||
            section.abbrev === prev.section
        ) ?? payload.sections![0];

      // Autofill DOI prefix, preserving suffix if present
      let newDoi = prefix;
      if (prefix && prev.doi) {
        const match = prev.doi.match(/^(10\.\d{4,9}\/)(.*)$/);
        if (match) {
          newDoi = prefix + match[2];
        } else if (!prev.doi.startsWith("10.")) {
          newDoi = prefix + prev.doi;
        }
      }

      return {
        ...prev,
        section: selected.title,
        sectionId: String(selected.id ?? ""),
        sectionAbbrev: selected.abbrev ?? "",
        doi: disabledDoi ? "" : newDoi,
        licenseUrl: payload.licenseUrl || prev.licenseUrl,
        publisherName: payload.publisherName || prev.publisherName,
        publisherCountry: payload.publisherCountry || prev.publisherCountry
      };
    });
    setDetailsFetched(true);
    return payload.pageNumbersAvailable
      ? `Fetched ${payload.sections.length} sections and page numbers for ${payload.issues?.length ?? 0} issues.`
      : `Fetched ${payload.sections.length} sections. Issue page-number lookup was unavailable.`;
  };

  type OjsSubmissionMatch = { id: number; title: string; stage: string; status: string; published: boolean };
  // Look the typed ID up in OJS and show what it actually is, so you can check
  // the article before publishing into it. The ID is the only thing that selects
  // a submission — nothing here matches on the document's title.
  const findOjsSubmission = async () => {
    const journal = production.appliedJournal || production.journal;
    if (!journal) throw new Error("Select a journal first.");
    const id = existingSubmissionId.trim();
    if (!/^\d+$/.test(id)) throw new Error("Type the OJS submission ID first, e.g. 899.");
    setSubmissionMatches([]);
    const response = await fetch(`${API_BASE}/find-ojs-submission`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ journal, submissionId: id })
    }).catch(() => {
      throw new Error("Editor service not reachable. Check your connection and try again.");
    });
    const payload = (await response.json().catch(() => null)) as { ok?: boolean; error?: string; matches?: OjsSubmissionMatch[] } | null;
    if (!response.ok || !payload?.ok) throw new Error(payload?.error || `OJS lookup failed (HTTP ${response.status}).`);
    const found = payload.matches?.[0];
    if (!found) throw new Error(`Submission ${id} was not found in this journal.`);
    setSubmissionMatches(payload.matches || []);
    return found.published
      ? `Submission ${found.id} is already published — publishing into it will fail. Use a different ID.`
      : `Submission ${found.id} found: ${found.stage} · ${found.status}. Check the title below before publishing.`;
  };

  const publishToOjs = async () => {
    if (!generatedPdf) throw new Error("Generate the PDF in the Export tab first, then publish.");
    const existingId = (production.ojsSubmissionId || existingSubmissionId).trim();
    if (existingId && !/^\d+$/.test(existingId)) throw new Error("Existing OJS submission ID must be a number, e.g. 693.");
    const journal = production.appliedJournal || production.journal;
    if (!journal) throw new Error("Select a journal first.");

    // Step 1: ensure the volume/issue exists. /create-issue is idempotent — it
    // returns the existing issue (created:false) or creates one (created:true).
    showNotice("Checking volume/issue on OJS…", "pending");
    const year = (production.publicationDate.split("-")[2] || "").trim();
    const issueRes = await fetch(`${API_BASE}/create-issue`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ journal, volume: production.volume, number: issueDisabled ? "" : production.issue, year })
    }).catch(() => {
      throw new Error("Editor service not reachable. Check your connection and publish again.");
    });
    const issuePayload = (await issueRes.json().catch(() => null)) as {
      ok?: boolean;
      created?: boolean;
      issue?: OjsIssue;
      error?: string;
    } | null;
    if (!issueRes.ok || !issuePayload?.ok) {
      throw new Error(issuePayload?.error || `Volume/issue check failed (HTTP ${issueRes.status}).`);
    }
    const issueId = issuePayload.issue?.id != null ? String(issuePayload.issue.id) : production.issueId;
    showNotice(
      issuePayload.created
        ? "Volume/issue not found — created. Publishing to volume/issue…"
        : "Volume/issue found. Publishing to volume/issue…",
      "pending"
    );

    // Step 2: publish into that issue.
    const article = await semanticExporter.buildArticleJson();
    const response = await fetch(`${API_BASE}/publish-ojs`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key: generatedPdf.key, production: { ...production, issueId }, article, existingSubmissionId: existingId || undefined })
    }).catch(() => {
      throw new Error("Editor service not reachable. Check your connection and publish again.");
    });
    const payload = (await response.json().catch(() => null)) as {
      ok?: boolean;
      message?: string;
      error?: string;
      url?: string;
      doiDeposit?: { attempted?: boolean; deposited?: boolean; doi?: string; status?: string; detail?: string };
    } | null;
    if (!response.ok || !payload?.ok) {
      throw new Error(payload?.error || `OJS publish failed with HTTP ${response.status}.`);
    }
    // The DOI is deposited with Crossref inside the same call, after the article
    // and its issue go live. Publishing succeeded either way, so a failed deposit
    // is reported in this banner rather than raised — the DOI can be deposited
    // from Distribution > DOIs in OJS.
    // Banner wording stays plain: one line per thing that happened, no OJS
    // internals. The Worker reports a step that failed AFTER the article was
    // created as "(WARNING: …)" inside its message rather than throwing (e.g.
    // the issue could not be published, leaving the article invisible), so those
    // have to show — but the DOI deposit reports itself through `doiDeposit`,
    // so its warning is dropped here to avoid saying the same thing twice.
    const warnings = (payload.message || "")
      .match(/\(WARNING:[^)]*\)/g)
      ?.filter((w) => !/\bDOI\b/i.test(w))
      .map((w) => {
        const text = w.replace(/^\(WARNING:\s*/, "").replace(/\)$/, "").trim();
        return text.charAt(0).toUpperCase() + text.slice(1);
      }) || [];
    const deposit = payload.doiDeposit;
    const lines = ["Article published successfully."];
    if (deposit?.attempted) {
      lines.push(deposit.deposited ? "DOI deposited." : `DOI not deposited — ${deposit.detail || deposit.status || "unknown error"}.`);
    }
    for (const warning of warnings) lines.push(warning);
    return {
      message: lines.join(" "),
      sticky: true,
      url: payload.url || ""
    };
  };

  const exportArticle = async (kind: "jats" | "xml" | "html") => {
    if (!exportArtifacts) {
      throw new Error("Exports are still preparing. Open Export tab and wait a moment.");
    }
    if (kind === "jats") saveAs(exportArtifacts.jats, "article.jats.xml");
    if (kind === "html") saveAs(exportArtifacts.html, "article.html");
  };

  const shortcutActions = useMemo<Record<TabValue, ShortcutAction[]>>(
    () => ({
      tag: [
        { key: "t", run: () => run("Title", applyTitle) },
        { key: "s", run: () => run("Subtitle", applySubtitle) },
        { key: "a", run: () => run("Author", applyAuthor) },
        { key: "b", run: () => run("Abstract", applyAbstract) },
        { key: "k", run: () => run("Keywords", applyKeywords) },
        { key: "1", run: () => run("Heading 1", applySection) },
        { key: "2", run: () => run("Heading 2", applySubsection) },
        { key: "3", run: () => run("Heading 3", applyMinorSection) },
        { key: "4", run: () => run("Heading 4", applyHeading4) },
        { key: "5", run: () => run("Heading 5", applyHeading5) },
        { key: "6", run: () => run("Heading 6", applyHeading6) },
        { key: "p", run: () => run("Paragraph", applyParagraph) },
        { key: "r", run: () => run("References", applyReferences) },
        { key: "x", run: () => run("Untag", () => wordService.removeTagAtSelection()) },
        { key: "l", run: loadAuthors },
        { key: "n", run: addAuthor },
        { key: "i", run: insertAuthors },
        { key: "u", run: updateAuthorsInDoc },
        { key: "o", run: () => run("Fetch Contributors", fetchContributorsFromOjs) }
      ],
      objects: [
        { key: "f", run: () => run("Figure", applyFigure) },
        { key: "c", run: () => run("Figure Caption", applyFigureCaption) },
        { key: "t", run: () => run("Table", applyTable) },
        { key: "y", run: () => run("Table Caption", applyTableCaption) },
        { key: "n", run: () => run("Table Notes", applyTableNotes) },
        { key: "e", run: () => run("Equation", applyEquation) },
        { key: "q", run: () => run("Block Quote", applyBlockQuote) },
        {
          key: "j",
          run: () =>
            run("Set Caption", async () => {
              await wordService.setCaptionForSelection(captionDraft);
              setCaptionDraft("");
            })
        }
      ],
      validate: [
        { key: "a", run: () => run("Validate Article", validateArticle) },
        { key: "t", run: () => run("Validate Tables", validateTables) },
        { key: "f", run: () => run("Find Untagged Text", () => validateArticle({ onlyUntaggedCandidates: true })) },
        { key: "n", run: () => run("Auto Number Figures", () => numberingService.autoNumberFigures()) },
        { key: "m", run: () => run("Auto Number Tables", () => numberingService.autoNumberTables()) },
        { key: "g", run: () => run("Generate TOC", () => numberingService.generateToc()) },
        { key: "r", run: () => run("Remove Lines", () => wordService.removeDecorativeLines()) },
        { key: "s", run: () => run("Update Styles", () => wordService.updateAllStyles()) }
      ],
      production: [
        { key: "a", run: applyProduction },
        { key: "u", run: updateProductionData },
        { key: "s", run: saveProduction },
        { key: "f", run: () => run("Fetching journal details", fetchOjsSections) }
      ],
      export: [
        { key: "j", run: () => run("Export JSON", exportJson) },
        { key: "x", run: () => run("Export XML", exportXml) },
        { key: "p", run: () => run("Export PDF", exportPdf) },
        { key: "v", run: () => run("Preview PDF", previewPdf) }
      ],
      publish: [
        { key: "v", run: () => run("Preview PDF", previewPdf) },
        { key: "p", run: () => run("Publish to OJS", publishToOjs) }
      ],
      contributors: [],
      tagstest: []
    }),
    // Action map intentionally refreshes on state used by action closures; wrapping every handler in useCallback adds noise here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authors, captionDraft, exportArtifacts, production, submissionIdDraft, doiDisabled]
  );

  useEffect(() => {
    void productionService
      .read()
      .then((saved) => {
        // Older saves stored the date as ISO (YYYY-MM-DD); the field is DD-MM-YYYY.
        const iso = saved.publicationDate.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
        setProduction(iso ? { ...saved, publicationDate: `${iso[3]}-${iso[2]}-${iso[1]}` } : saved);
        // A doc with production already applied should show its fields immediately,
        // without forcing a re-fetch just to view what's there.
        if (saved.appliedJournal?.trim()) setDetailsFetched(true);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      // Control+Option (Mac: ctrlKey + altKey) / Ctrl+Alt (Windows) — was
      // Cmd+Shift, remapped because it collided with other app shortcuts.
      if (!event.ctrlKey || !event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target) {
        const tagName = target.tagName;
        if (tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT" || target.isContentEditable) return;
      }
      const key = event.key.toLowerCase();
      const action = shortcutActions[tab].find((entry) => entry.key === key);
      if (!action) return;
      event.preventDefault();
      action.run();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [shortcutActions, tab]);

  return (
    <div className={styles.shell}>
      <header className={styles.header}>
        <Title3>Libertatem Editorial Suite</Title3>
        <Text block>Semantic tagging for journal production.</Text>
      </header>
      <main className={styles.main}>
        <TabList selectedValue={tab} onTabSelect={(_, data) => setTab(data.value as TabValue)}>
          <Tab value="tag">Tag</Tab>
          <Tab value="production">Production</Tab>
          <Tab value="contributors">Contributors</Tab>
          <Tab value="validate">Validate</Tab>
          <Tab value="export">Export</Tab>
          <Tab value="publish">Publish</Tab>
        </TabList>

        {tab === "tag" && (
          <>
            <section className={styles.section}>
              <Text weight="semibold" size={400}>Auto-Tag</Text>
              <Text block size={200} style={{ marginBottom: "8px" }}>
                Scans the entire document. Detects title, authors, headings, abstract, keywords, paragraphs, figures, tables, references, equations, and footnotes. Skips already-tagged.
              </Text>
              <Button
                className={styles.actionButton}
                appearance="primary"
                onClick={() => run("Auto-Tag Document", async () => {
                  // Runs as its own isolated pass first — see healEquationTags
                  // for why it must not share autoTagDocument's single batch.
                  const heal = await wordService.healEquationTags();
                  const result = await wordService.autoTagDocument();
                  const parts = [
                    result.headings && `${result.headings} headings`,
                    result.authors && `${result.authors} author lines`,
                    result.paragraphs && `${result.paragraphs} paragraphs`,
                    result.figures && `${result.figures} figures`,
                    result.captions && `${result.captions} captions`,
                    result.tables && `${result.tables} tables`,
                    result.blockquotes && `${result.blockquotes} blockquotes`,
                    result.footnotes && `${result.footnotes} footnotes`,
                  ].filter(Boolean);
                  return `Tagged ${parts.join(", ") || "nothing new"} [eq-heal: ${heal.healed} healed, ${heal.reverted} reverted; new-eq:${result.equations}]. Skipped ${result.skipped} already-tagged.`;
                })}
              >
                Auto-Tag Document
              </Button>
            </section>

            <section className={styles.section}>
              <Text weight="semibold">Structure</Text>
              <div className={styles.grid}>
                <Button className={styles.actionButton} title={shortcutTitle("Title", "t")} onClick={() => run("Title", applyTitle)}>{buttonText("Title", "t")}</Button>
                <Button className={styles.actionButton} title={shortcutTitle("Subtitle", "s")} onClick={() => run("Subtitle", applySubtitle)}>{buttonText("Subtitle", "s")}</Button>
                <Button className={styles.actionButton} title={shortcutTitle("Author", "a")} onClick={() => run("Author", applyAuthor)}>{buttonText("Author", "a")}</Button>
                <Button className={styles.actionButton} title={shortcutTitle("Abstract", "b")} onClick={() => run("Abstract", applyAbstract)}>{buttonText("Abstract", "b")}</Button>
                <Button className={styles.actionButton} title={shortcutTitle("Keywords", "k")} onClick={() => run("Keywords", applyKeywords)}>{buttonText("Keywords", "k")}</Button>
                <Button className={styles.actionButton} title={shortcutTitle("Heading 1", "1")} onClick={() => run("Heading 1", applySection)}>{buttonText("Heading 1", "1")}</Button>
                <Button className={styles.actionButton} title={shortcutTitle("Heading 2", "2")} onClick={() => run("Heading 2", applySubsection)}>{buttonText("Heading 2", "2")}</Button>
                <Button className={styles.actionButton} title={shortcutTitle("Heading 3", "3")} onClick={() => run("Heading 3", applyMinorSection)}>{buttonText("Heading 3", "3")}</Button>
                <Button className={styles.actionButton} title={shortcutTitle("Heading 4", "4")} onClick={() => run("Heading 4", applyHeading4)}>{buttonText("Heading 4", "4")}</Button>
                <Button className={styles.actionButton} title={shortcutTitle("Heading 5", "5")} onClick={() => run("Heading 5", applyHeading5)}>{buttonText("Heading 5", "5")}</Button>
                <Button className={styles.actionButton} title={shortcutTitle("Heading 6", "6")} onClick={() => run("Heading 6", applyHeading6)}>{buttonText("Heading 6", "6")}</Button>
                <Button className={styles.actionButton} title={shortcutTitle("Paragraph", "p")} onClick={() => run("Paragraph", applyParagraph)}>{buttonText("Paragraph", "p")}</Button>
                <Button className={styles.actionButton} title={shortcutTitle("References", "r")} onClick={() => run("References", applyReferences)}>{buttonText("References", "r")}</Button>
              </div>
              <Button
                appearance="primary"
                className={[styles.dangerButton, styles.actionButton].join(" ")}
                title={shortcutTitle("Untag selection", "x")}
                onClick={() => run("Untag", () => wordService.removeTagAtSelection())}
              >
                {buttonText("Untag selection", "x", undefined, true)}
              </Button>
            </section>

            <section className={styles.section}>
              <Text weight="semibold">Objects</Text>
              <div className={styles.grid}>
                <Button className={styles.actionButton} title={shortcutTitle("Figure", "f")} onClick={() => run("Figure", applyFigure)}>{buttonText("Figure", "f")}</Button>
                <Button className={styles.actionButton} title={shortcutTitle("Figure Caption", "c")} onClick={() => run("Figure Caption", applyFigureCaption)}>{buttonText("Figure Caption", "c")}</Button>
                <Button className={styles.actionButton} title={shortcutTitle("Table", "t")} onClick={() => run("Table", applyTable)}>{buttonText("Table", "t")}</Button>
                <Button className={styles.actionButton} title={shortcutTitle("Table Caption", "y")} onClick={() => run("Table Caption", applyTableCaption)}>{buttonText("Table Caption", "y")}</Button>
                <Button className={styles.actionButton} title={shortcutTitle("Table Notes", "n")} onClick={() => run("Table Notes", applyTableNotes)}>{buttonText("Table Notes", "n")}</Button>
                <Button className={styles.actionButton} title={shortcutTitle("Equation", "e")} onClick={() => run("Equation", applyEquation)}>{buttonText("Equation", "e")}</Button>
                <Button className={styles.actionButton} title={shortcutTitle("Block Quote", "q")} onClick={() => run("Block Quote", applyBlockQuote)}>{buttonText("Block Quote", "q")}</Button>
              </div>
              <Field label="Caption for selected figure / table" hint="Click inside a figure or table, type its caption, then Set Caption.">
                <Input value={captionDraft} placeholder="Caption text" onChange={(_, d) => setCaptionDraft(d.value)} />
              </Field>
              <Button
                className={styles.actionButton}
                title={shortcutTitle("Set Caption", "j")}
                onClick={() =>
                  run("Set Caption", async () => {
                    await wordService.setCaptionForSelection(captionDraft);
                    setCaptionDraft("");
                  })
                }
              >
                {buttonText("Set Caption", "j")}
              </Button>
            </section>
          </>
        )}

        {tab === "contributors" && (
          <section className={styles.section}>
            <div className={styles.publishCard}>
              <Text weight="semibold">Already in OJS? Load its contributors</Text>
              <Text size={200} className={styles.publishHint}>
                {production.appliedJournal || production.journal
                  ? `Searches ${production.appliedJournal || production.journal}. Loading replaces the authors below with what OJS holds, and keeps each contributor's OJS id so publishing updates them instead of adding copies.`
                  : "Select the journal in the Production tab first — a submission ID only means something inside one journal."}
              </Text>
              {production.ojsSubmissionId ? (
                <div className={styles.stack}>
                  <Field label="OJS submission (locked to this paper)">
                    <Input value={production.ojsSubmissionId} disabled readOnly />
                  </Field>
                  <Button className={styles.actionButton} title="Release the lock so a different submission can be fetched" onClick={unlockOjsSubmission}>
                    Unlock submission
                  </Button>
                </div>
              ) : (
                <div className={styles.publishRow}>
                  <Input
                    value={submissionIdDraft}
                    placeholder="Submission ID, e.g. 899"
                    inputMode="numeric"
                    disabled={!(production.appliedJournal || production.journal)}
                    onChange={(_, data) => setSubmissionIdDraft(data.value.replace(/[^\d]/g, ""))}
                  />
                  <Button
                    appearance="primary"
                    disabled={!(production.appliedJournal || production.journal)}
                    title={shortcutTitle("Fetch contributors from OJS", "o")}
                    onClick={() => run("Fetch Contributors", fetchContributorsFromOjs)}
                  >
                    Fetch Contributors
                  </Button>
                </div>
              )}
              {ojsSubmissionInfo && (
                <div className={styles.matchList}>
                  <div className={styles.matchCard} title={ojsSubmissionInfo.published ? "Already published" : "Contributors were loaded from this submission"}>
                    <span className={styles.matchId}>#{ojsSubmissionInfo.id}</span>
                    <span className={styles.matchMeta}>{ojsSubmissionInfo.stage} · {ojsSubmissionInfo.status}</span>
                    <span className={styles.matchTitle}>{ojsSubmissionInfo.title}</span>
                  </div>
                </div>
              )}
              {production.ojsSubmissionId && (
                <Text size={200} className={styles.publishTargetOk}>
                  Publish will target submission #{production.ojsSubmissionId}. Edit the authors below, then Insert/Update in the
                  document so the changes reach the PDF and OJS.
                </Text>
              )}
            </div>
            <div className={[styles.splitRow, styles.splitRowSpaced].join(" ")}>
              <Text className={styles.splitRowLabel} weight="semibold">Authors</Text>
              <Button className={[styles.actionButton, styles.splitRowButton].join(" ")} title={shortcutTitle("Load authors", "l")} onClick={loadAuthors}>{buttonText("Load authors", "l")}</Button>
            </div>
            {authors.map((author, index) => {
              const orcidCheck = normalizeOrcid(author.orcid);
              const rorCheck = normalizeRor(author.ror);
              const emailCheck = validateEmail(author.email);
              return (
                <div key={index} className={styles.authorCard}>
                  <div className={styles.authorCardHead}>
                    <Text size={200} weight="semibold">Author {index + 1}</Text>
                    {authors.length > 1 && (
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<DeleteRegular />}
                        aria-label="Remove author"
                        onClick={() => removeAuthor(index)}
                      />
                    )}
                  </div>
                  <Field label="Name">
                    <Input value={author.name} placeholder="Jane A. Doe" onChange={(_, d) => updateAuthor(index, "name", d.value)} />
                  </Field>
                  <Field label="Designation / Affiliation">
                    {/* A contributor fetched from OJS with nothing in this
                        field says so, rather than looking like the fetch dropped
                        it. It stays a placeholder, never a value, so it cannot be
                        written into the document or back to OJS by accident. */}
                    <Input
                      value={author.affiliation}
                      placeholder={
                        author.ojsContributorId != null && !author.affiliation
                          ? "Not available in OJS — type it here"
                          : "Dept. of Law, University of X"
                      }
                      onChange={(_, d) => updateAuthor(index, "affiliation", d.value)}
                    />
                  </Field>
                  <Field
                    label="Email (optional)"
                    validationState={author.email && !emailCheck.ok ? "error" : "none"}
                    validationMessage={author.email && !emailCheck.ok ? emailCheck.error : undefined}
                  >
                    <Input value={author.email} placeholder="jane.doe@university.edu" onChange={(_, d) => updateAuthor(index, "email", d.value)} />
                  </Field>
                  <Field
                    label="ORCID (optional)"
                    validationState={author.orcid && !orcidCheck.ok ? "error" : "none"}
                    validationMessage={author.orcid && !orcidCheck.ok ? orcidCheck.error : undefined}
                  >
                    <Input value={author.orcid} placeholder="https://orcid.org/0000-0000-0000-0000" onChange={(_, d) => updateAuthor(index, "orcid", d.value)} />
                  </Field>
                  <Field
                    label="ROR ID (optional)"
                    validationState={author.ror && !rorCheck.ok ? "error" : "none"}
                    validationMessage={author.ror && !rorCheck.ok ? rorCheck.error : undefined}
                  >
                    <Input value={author.ror} placeholder="https://ror.org/04xxxxxxx" onChange={(_, d) => updateAuthor(index, "ror", d.value)} />
                  </Field>
                  <Field label="CRediT Roles (optional)">
                    <Dropdown
                      multiselect
                      placeholder="Select roles"
                      value={author.roles?.join(", ") ?? ""}
                      selectedOptions={author.roles ?? []}
                      onOptionSelect={(_, data) => {
                        updateAuthor(index, "roles", data.selectedOptions);
                      }}
                    >
                      {CREDIT_ROLES.map((role) => (
                        <Option key={role} value={role}>
                          {role}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>
                </div>
              );
            })}
            <Button className={[styles.actionButton, styles.addContributorButton].join(" ")} title={shortcutTitle("Add new Contributor", "n")} onClick={addAuthor}>{buttonText("Add new Contributor", "n", <AddRegular />)}</Button>
            <div className={styles.stack}>
              <Button className={styles.actionButton} appearance="primary" title={shortcutTitle("Insert contributor in document", "i")} onClick={insertAuthors}>{buttonText("Insert contributor in document", "i", undefined, true)}</Button>
              <Button className={styles.actionButton} title={shortcutTitle("Update authors in document", "u")} onClick={updateAuthorsInDoc}>{buttonText("Update authors in document", "u")}</Button>
            </div>
          </section>
        )}

        {tab === "validate" && (
          <section className={styles.section}>
            <div className={styles.singleColumnGrid}>
              <Button className={styles.actionButton} title={shortcutTitle("Validate Article", "a")} onClick={() => run("Validate Article", validateArticle)}>{buttonText("Validate Article", "a", <CheckmarkCircleRegular />)}</Button>
              <Button className={styles.actionButton} title={shortcutTitle("Validate Tables", "t")} onClick={() => run("Validate Tables", validateTables)}>{buttonText("Validate Tables", "t", <CheckmarkCircleRegular />)}</Button>
              <Button className={styles.actionButton} title={shortcutTitle("Find Untagged Text", "f")} onClick={() => run("Find Untagged Text", () => validateArticle({ onlyUntaggedCandidates: true }))}>{buttonText("Find Untagged Text", "f", <DocumentSearchRegular />)}</Button>
              <Button className={styles.actionButton} title={shortcutTitle("Auto Number Figures", "n")} onClick={() => run("Auto Number Figures", () => numberingService.autoNumberFigures())}>{buttonText("Auto Number Figures", "n", <NumberSymbolRegular />)}</Button>
              <Button className={styles.actionButton} title={shortcutTitle("Auto Number Tables", "m")} onClick={() => run("Auto Number Tables", () => numberingService.autoNumberTables())}>{buttonText("Auto Number Tables", "m", <NumberSymbolRegular />)}</Button>
              <Button className={styles.actionButton} title={shortcutTitle("Generate TOC", "g")} onClick={() => run("Generate TOC", () => numberingService.generateToc())}>{buttonText("Generate TOC", "g", <DocumentBulletListRegular />)}</Button>
              <Button className={styles.actionButton} title={shortcutTitle("Remove Lines", "r")} onClick={() => run("Remove Lines", () => wordService.removeDecorativeLines())}>{buttonText("Remove Lines", "r", <TextBulletListSquareRegular />)}</Button>
              <Button className={styles.actionButton} title={shortcutTitle("Update Styles", "s")} onClick={() => run("Update Styles", () => wordService.updateAllStyles())}>{buttonText("Update Styles", "s", <PaintBrushRegular />)}</Button>
            </div>
            <div className={styles.issueList}>
              {issues.map((issue) => (
                <button
                  key={`${issue.code}-${issue.location}-${issue.message}`}
                  type="button"
                  className={styles.issue}
                  onClick={() => run("Go to issue", () => wordService.focusValidationIssue(issue))}
                >
                  <Text weight="semibold">{issue.severity.toUpperCase()} · {issue.code}</Text>
                  <Text block>{issue.message}</Text>
                  {issue.location && <Text block size={200}>{issue.location}</Text>}
                </button>
              ))}
            </div>
          </section>
        )}

        {tab === "production" && (
          <section className={styles.section}>
            {journalDropdown()}
            {!detailsFetched ? (
              <Button
                className={styles.actionButton}
                appearance="primary"
                style={{ marginTop: "12px" }}
                disabled={!production.journal}
                title={shortcutTitle("Fetch journal details", "f")}
                onClick={() => run("Fetching journal details", fetchOjsSections)}
              >
                {buttonText("Fetch Journal Details", "f")}
              </Button>
            ) : (
              <>
                <Field label="Volume / issue">
                  <Dropdown
                    value={issueChoiceLabel}
                    selectedOptions={issueChoice ? [issueChoice] : []}
                    placeholder={issueOptions.length > 0 ? "Pick an existing issue, or create a new one" : "Create a new volume / issue"}
                    onOptionSelect={(_, data) => {
                      const choice = String(data.optionValue ?? "");
                      setIssueChoice(choice);
                      if (choice === NEW_ISSUE_CHOICE) {
                        // Fresh volume/issue: clear everything the old selection
                        // filled in, so nothing from another issue leaks in. Page
                        // one is where a new issue starts, and the DOI stays empty
                        // until Volume/Issue and the Publication Date are typed —
                        // recomputeDoiSuggestion fills it from there.
                        setProduction((prev) => ({
                          ...prev,
                          volume: "",
                          issue: "",
                          issueId: "",
                          startPage: "1",
                          endPage: "",
                          publicationDate: "",
                          doi: ""
                        }));
                        return;
                      }
                      const iss = issueOptions[Number(choice)];
                      if (iss) {
                        setProduction((prev) => ({
                          ...prev,
                          volume: iss.volume,
                          issue: issueDisabled ? "" : iss.number,
                          issueId: String(iss.id ?? ""),
                          startPage: iss.nextStartPage != null ? String(iss.nextStartPage) : "",
                          publicationDate: iss.suggestedPublicationDate ?? "",
                          doi: iss.nextDoi ?? "",
                          endPage: ""
                        }));
                      }
                    }}
                  >
                    <Option value={NEW_ISSUE_CHOICE} text={NEW_ISSUE_LABEL}>{NEW_ISSUE_LABEL}</Option>
                    {issueOptions.map((iss, idx) => (
                      <Option key={idx} value={String(idx)} text={issueLabel(iss)}>{issueLabel(iss)}</Option>
                    ))}
                  </Dropdown>
                  {issueChoice === NEW_ISSUE_CHOICE && (
                    <Text size={200}>
                      Type the new Volume{issueDisabled ? "" : " and Issue"} and the Publication Date below — the DOI fills in
                      once they are set. The issue is created on OJS (and published, without e-mailing readers) when you publish.
                    </Text>
                  )}
                </Field>
                <div className={styles.grid}>
                  <Field label="Section">
                    {sectionOptions.length > 0 ? (
                      <Dropdown
                        value={production.section}
                        selectedOptions={production.section ? [production.section] : []}
                        placeholder="Select section"
                        onOptionSelect={(_, data) => {
                          const selected = sectionOptions.find((section) => section.title === String(data.optionValue ?? ""));
                          if (selected) updateProductionSection(selected);
                        }}
                      >
                        {sectionOptions.map((section) => (
                          <Option key={`${section.id ?? section.title}`} value={section.title} text={section.title}>
                            {section.abbrev ? `${section.title} (${section.abbrev})` : section.title}
                          </Option>
                        ))}
                      </Dropdown>
                    ) : (
                      <Input
                        value={production.section}
                        placeholder="Articles"
                        onChange={(_, d) =>
                          setProduction((prev) => ({ ...prev, section: d.value, sectionId: "", sectionAbbrev: "" }))
                        }
                      />
                    )}
                  </Field>
                  {!doiDisabled && (
                    <Field label="DOI">
                      <Input value={production.doi} placeholder="10.xxxx/xxxxx" onChange={(_, d) => updateProduction("doi", d.value)} />
                    </Field>
                  )}
                  <Field label="Volume">
                    <Input
                      value={production.volume}
                      onChange={(_, d) => updateProduction("volume", d.value)}
                      onBlur={recomputeDoiSuggestion}
                    />
                  </Field>
                  {!issueDisabled && (
                    <Field label="Issue">
                      <Input
                        value={production.issue}
                        onChange={(_, d) => updateProduction("issue", d.value)}
                        onBlur={recomputeDoiSuggestion}
                      />
                    </Field>
                  )}
                  <Field label="Start Page">
                    <Input value={production.startPage} onChange={(_, d) => updateStartPage(d.value)} />
                  </Field>
                  <Field
                    label="Publication Date [DD-MM-YYYY]"
                    validationState={dateError ? "error" : "none"}
                    validationMessage={dateError ? dateErrorMessage : undefined}
                  >
                    <div className={styles.dateRow}>
                      <Input
                        ref={dayInputRef}
                        className={[
                          styles.dateSegmentDay,
                          dateError ? styles.dateErrorInput : "",
                          dateShaking ? styles.shakeError : ""
                        ].filter(Boolean).join(" ")}
                        placeholder="DD"
                        value={pubDay}
                        maxLength={2}
                        onChange={(_, d) => updatePublicationDatePart("day", d.value)}
                        onBlur={(e) => handlePublicationDateBlur("day", e)}
                        onAnimationEnd={() => setDateShaking(false)}
                      />
                      <span className={styles.dateSegmentSeparator}>-</span>
                      <Input
                        ref={monthInputRef}
                        className={[
                          styles.dateSegmentMonth,
                          dateError ? styles.dateErrorInput : "",
                          dateShaking ? styles.shakeError : ""
                        ].filter(Boolean).join(" ")}
                        placeholder="MM"
                        value={pubMonth}
                        maxLength={2}
                        onChange={(_, d) => updatePublicationDatePart("month", d.value)}
                        onKeyDown={(e) => handlePublicationDateSegmentKeyDown("month", e)}
                        onBlur={(e) => handlePublicationDateBlur("month", e)}
                        onAnimationEnd={() => setDateShaking(false)}
                      />
                      <span className={styles.dateSegmentSeparator}>-</span>
                      <Input
                        ref={yearInputRef}
                        className={[
                          styles.dateSegmentYear,
                          dateError ? styles.dateErrorInput : "",
                          dateShaking ? styles.shakeError : ""
                        ].filter(Boolean).join(" ")}
                        placeholder="YYYY"
                        value={pubYear}
                        maxLength={4}
                        onChange={(_, d) => updatePublicationDatePart("year", d.value)}
                        onKeyDown={(e) => handlePublicationDateSegmentKeyDown("year", e)}
                        onBlur={(e) => handlePublicationDateBlur("year", e)}
                        onAnimationEnd={() => setDateShaking(false)}
                      />
                    </div>
                  </Field>
                  {!doiDisabled && (
                    <Field label="Crossmark">
                      <Checkbox
                        className={styles.crossmarkCheckbox}
                        label="Show button in PDF"
                        checked={!!production.showCrossmark}
                        onChange={(_, d) => setProduction((prev) => ({ ...prev, showCrossmark: !!d.checked }))}
                      />
                    </Field>
                  )}
                </div>
                <Field label="License URL">
                  <Input value={production.licenseUrl} placeholder="https://creativecommons.org/licenses/by/4.0/" onChange={(_, d) => updateProduction("licenseUrl", d.value)} />
                </Field>
                {(production.publisherName || production.publisherCountry) && (
                  <div className={styles.grid}>
                    <Field label="Publisher">
                      <Input value={production.publisherName} disabled readOnly />
                    </Field>
                    <Field label="Country">
                      <Input value={production.publisherCountry} disabled readOnly />
                    </Field>
                  </div>
                )}
                <div className={styles.stack}>
                  <Button className={styles.actionButton} appearance="primary" title={shortcutTitle("Apply Production", "a")} onClick={applyProduction}>
                    {buttonText("Apply Production", "a", undefined, true)}
                  </Button>
                  <Button className={styles.actionButton} title="Re-apply corrected production details to the header, footer and DOI" onClick={updateProductionData}>
                    {buttonText("Update Production Data", "u")}
                  </Button>
                  <Button className={styles.actionButton} title={shortcutTitle("Save Production", "s")} onClick={saveProduction}>
                    {buttonText("Save Production", "s")}
                  </Button>
                  <Button className={styles.dangerButton} title="Remove the applied header, footer and DOI, and clear saved production data" onClick={removeProductionData}>
                    Remove Production Data
                  </Button>
                </div>
              </>
            )}
          </section>
        )}

        {tab === "export" && (
          <section className={styles.section}>
            <div className={styles.grid}>
              <Button className={styles.actionButton} title={shortcutTitle("Export JSON", "j")} onClick={() => run("Export JSON", exportJson)}>{buttonText("Export JSON", "j")}</Button>
              <Button className={styles.actionButton} title="Export JATS XML is not active yet." disabled>{buttonText("Export JATS XML", "t")}</Button>
              <Button className={styles.actionButton} title={shortcutTitle("Export XML", "x")} disabled={!exportArtifacts} onClick={() => run("Export XML", exportXml)}>{buttonText("Export XML", "x")}</Button>
              <Button className={styles.actionButton} title="Export HTML is not active yet." disabled>{buttonText("Export HTML", "h")}</Button>
              <Button className={styles.actionButton} title={shortcutTitle("Export PDF", "p")} onClick={() => run("Export PDF", exportPdf)}>{buttonText("Export PDF", "p")}</Button>
              <Button className={styles.actionButton} title="Preview the generated PDF" disabled={!generatedPdf} onClick={() => run("Preview PDF", previewPdf)}>{buttonText("Preview PDF", "v")}</Button>
            </div>
            <Button
              className={styles.dangerButton}
              disabled={!generatedPdf}
              title="Delete the currently generated PDF from R2 storage"
              onClick={() => run("Discard Generated PDF", discardGeneratedPdf)}
            >
              Discard Generated PDF from Storage
            </Button>
          </section>
        )}

        {tab === "publish" && (
          <section className={styles.section}>
            <div className={styles.stack}>
              {production.appliedJournal ? (
                <Field label="Journal (locked)">
                  <Input value={production.appliedJournal} disabled readOnly />
                  <Text block size={200}>
                    Locked to this journal because production is applied. To change journals, go to Production and
                    Update (different journal) or Remove Production Data, then re-apply.
                  </Text>
                </Field>
              ) : (
                journalDropdown()
              )}
              {generatedPdf ? (
                <div className={styles.stack}>
                  <Text block size={200}>PDF ready to publish ✓</Text>
                  <Button className={styles.actionButton} title="Preview the generated PDF" onClick={() => run("Preview PDF", previewPdf)}>
                    {buttonText("Preview PDF", "v")}
                  </Button>
                </div>
              ) : (
                <Text block size={200}>Generate a PDF in the Export tab first.</Text>
              )}
              {production.ojsSubmissionId ? (
                <div className={styles.publishCard}>
                  <Field label="OJS submission (locked)">
                    <Input value={production.ojsSubmissionId} disabled readOnly />
                  </Field>
                  <Text size={200} className={styles.publishTargetOk}>
                    Locked by Contributors &gt; Fetch Contributors. Its contributors are updated by their OJS ids — no duplicates.
                    Unlock it in the Contributors tab to target a different submission.
                  </Text>
                </div>
              ) : (
              <div className={styles.publishCard}>
                <Text weight="semibold">Already in the OJS pipeline? (optional)</Text>
                <Text size={200} className={styles.publishHint}>
                  If the author already submitted (and paid) in OJS, publish into that submission. Leave blank to create a new one.
                </Text>
                <div className={styles.publishRow}>
                  <Input
                    value={existingSubmissionId}
                    placeholder="Submission ID, e.g. 693"
                    inputMode="numeric"
                    onChange={(_, data) => {
                      setExistingSubmissionId(data.value.replace(/[^\d]/g, ""));
                      setSubmissionMatches([]);
                    }}
                  />
                  <Button title="Look this submission ID up in the journal's OJS and show which article it is" onClick={() => run("Find in OJS", findOjsSubmission)}>
                    Find by ID
                  </Button>
                </div>
                {submissionMatches.length > 0 && (
                  <div className={styles.matchList}>
                    {submissionMatches.map((m) => (
                      <div key={m.id} className={styles.matchCard} title={m.published ? "Already published" : "This is the submission that will be published into"}>
                        <span className={styles.matchId}>#{m.id}</span>
                        <span className={styles.matchMeta}>{m.stage} · {m.status}</span>
                        <span className={styles.matchTitle}>{m.title}</span>
                      </div>
                    ))}
                  </div>
                )}
                <Text size={200} className={existingSubmissionId ? styles.publishTargetOk : styles.publishHint}>
                  {existingSubmissionId
                    ? `Will publish into existing submission #${existingSubmissionId}. Its authors are matched and updated; its payment record is kept.`
                    : "No ID entered: a new OJS submission will be created (no payment record). Enter the ID to publish into an existing one."}
                </Text>
              </div>
              )}
              <Button className={styles.actionButton} appearance="primary" disabled={!generatedPdf} title={shortcutTitle("Publish to OJS", "p")} onClick={() => run("Publish to OJS", publishToOjs)}>
                {buttonText("Publish to OJS", "p", undefined, true)}
              </Button>
            </div>
          </section>
        )}


        {notice.visible && (
          <div className={styles.toastWrap}>
            <div
              className={[
                styles.toast,
                notice.tone === "success" ? styles.toastSuccess : notice.tone === "error" ? styles.toastError : styles.toastPending
              ].join(" ")}
            >
              {notice.tone === "pending" && <div className={styles.toastIndeterminate} />}
              <div className={styles.toastContent}>
                <Text weight="semibold">{notice.tone === "success" ? "Success" : notice.tone === "error" ? "Error" : "Working"}</Text>
                <Text>{notice.message}</Text>
                {notice.url ? (
                  <div className={styles.toastUrlBar}>
                    <Text className={styles.toastUrlText} title={notice.url}>{notice.url}</Text>
                    <Button
                      appearance="subtle"
                      className={styles.toastUrlButton}
                      icon={<CopyRegular />}
                      aria-label="Copy live URL"
                      onClick={() => void copyText(notice.url!)}
                    />
                    <a
                      className={styles.toastOpenLink}
                      href={notice.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Open URL in browser"
                      title="Open in browser"
                    >
                      <OpenRegular />
                      <span>Open</span>
                    </a>
                  </div>
                ) : null}
              </div>
              <Button
                appearance="subtle"
                className={styles.toastDismiss}
                icon={<DismissRegular />}
                aria-label="Dismiss message"
                onClick={hideNotice}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
